import { useState, useEffect, useCallback, useRef, useContext, useMemo } from 'react';
import { toast } from 'react-toastify';
import SensorConnectionContext from './SensorConnectionContext';
import SerialClient from '../services/serialClient';
import { createConnectionHeartbeat } from '../services/firmware/connectionHeartbeat';
import { createReconnectWatchdog } from '../services/firmware/reconnectWatchdog';
import useAuth from '../hooks/useAuth';

const LOG_CAP = 500;
const HEARTBEAT_INTERVAL_MS = 5000;
const HEARTBEAT_MAX_MISSES = 2;

/**
 * Owns the Sensor Manager's Web Serial connection above the router so it
 * survives page navigation. Consumers reach it via useSensorConnection().
 *
 * Steady-state liveness lives here too, globally:
 *   - a PING heartbeat while connected; a run of misses declares the link lost
 *     (flip badge, toast, tear down) — fixing the stale-badge-on-unplug gap;
 *   - an armed reconnect watchdog while disconnected that silently reconnects
 *     when the sensor returns. It is armed ONLY by an unexpected loss (not by a
 *     manual disconnect), so it never opens a serial port on a cold page load
 *     and never fights an intentional Disconnect.
 *
 * User-driven connection transitions (first manual connect, the firmware
 * flash/reboot dance, the update wizard) stay on the Sensor Manager page. That
 * page calls suspend()/resume() around the wizard and a flash so this monitor
 * stands down while it drives the port itself.
 */
export default function SensorConnectionProvider({
  children,
  heartbeatIntervalMs = HEARTBEAT_INTERVAL_MS,
  heartbeatMaxMisses = HEARTBEAT_MAX_MISSES,
}) {
  const [client, setClient] = useState(() => new SerialClient());
  const [connected, setConnected] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [serialLog, setSerialLog] = useState([]);
  // Frozen while the page drives the port itself (flash / update wizard):
  // pauses BOTH heartbeat and watchdog so we never re-open the port under
  // esp-web-tools or ping mid-flash.
  const [monitorSuspended, setMonitorSuspended] = useState(false);
  // Armed only by a heartbeat-detected loss; reset on every (re)connect. Gates
  // the background reconnect watchdog.
  const [autoReconnectEnabled, setAutoReconnectEnabled] = useState(false);

  const { user } = useAuth();

  const appendLog = useCallback((entry) => {
    setSerialLog((prev) => {
      const next = [...prev, entry];
      return next.length > LOG_CAP ? next.slice(-LOG_CAP) : next;
    });
  }, []);

  // (Re)subscribe to the active client's log stream whenever it changes.
  useEffect(() => {
    if (!client) return undefined;
    const unsubscribe = client.onLog(appendLog);
    return unsubscribe;
  }, [client, appendLog]);

  const adoptClient = useCallback((newClient) => {
    setClient(newClient);
  }, []);

  const disconnect = useCallback(async () => {
    try {
      await client.disconnect();
    } catch (_) {
      // best effort
    }
    setAutoReconnectEnabled(false); // intentional disconnect — do not auto-reconnect
    setConnected(false);
    setDeviceInfo(null);
  }, [client]);

  const suspend = useCallback(() => setMonitorSuspended(true), []);
  const resume = useCallback(() => setMonitorSuspended(false), []);

  // What "the heartbeat gave up" means: tear down the dead port, clear the
  // badge, arm auto-reconnect, and notify once. Named so the heartbeat effect
  // stays a thin wiring layer and the loss contract is testable in isolation.
  const handleHeartbeatLoss = useCallback(async () => {
    try {
      await client.forceClose?.();
    } catch (_) {
      // device is already gone — best effort
    }
    setConnected(false);
    setDeviceInfo(null);
    setAutoReconnectEnabled(true);
    toast.warn('Sensor disconnected', {
      toastId: 'sensor-disconnected',
      containerId: 'sensor-connection',
    });
  }, [client]);

  // A successful (re)connect clears the auto-reconnect arm, so a later manual
  // Disconnect is respected. Only a fresh heartbeat loss re-arms it.
  useEffect(() => {
    if (connected) setAutoReconnectEnabled(false);
  }, [connected]);

  // ---- Liveness heartbeat (while connected) ----
  useEffect(() => {
    if (!connected || monitorSuspended) return undefined;
    if (typeof client?.ping !== 'function') return undefined;

    const heartbeat = createConnectionHeartbeat({
      ping: () => client.ping(),
      onLost: handleHeartbeatLoss,
      intervalMs: heartbeatIntervalMs,
      maxMisses: heartbeatMaxMisses,
    });
    heartbeat.start();
    return () => heartbeat.stop();
  }, [connected, monitorSuspended, client, handleHeartbeatLoss, heartbeatIntervalMs, heartbeatMaxMisses]);

  // ---- Background reconnect (silent, no user gesture) ----
  // Establishes a fresh connection to a previously-granted port: a new client,
  // enough of a load to light the badge (info), then adopt + mark connected.
  const connectingRef = useRef(false);
  const connectPort = useCallback(async (port) => {
    if (connectingRef.current) return;
    connectingRef.current = true;
    try {
      const newClient = new SerialClient();
      await newClient.connect(port);
      const info = await newClient.info();
      setDeviceInfo({ ...info, protocolVersion: newClient.protocolVersion?.() ?? null });
      adoptClient(newClient);
      setConnected(true);
    } catch (_) {
      // Reconnect attempt failed (device still gone). Stay disconnected; the
      // watchdog keeps its 'connect' listener alive to catch the next replug.
    } finally {
      connectingRef.current = false;
    }
  }, [adoptClient]);

  useEffect(() => {
    if (connected || monitorSuspended || !autoReconnectEnabled) return undefined;
    if (typeof navigator === 'undefined' || !navigator.serial) return undefined;

    const watchdog = createReconnectWatchdog({
      nav: navigator,
      onReconnect: (port) => { connectPort(port); },
    });
    watchdog.start();
    return () => watchdog.stop();
  }, [connected, monitorSuspended, autoReconnectEnabled, connectPort]);

  // Logout: drop the hardware session when the user session ends. Only fire on
  // a real transition from signed-in to signed-out, not on the initial null.
  const prevUser = useRef(user);
  useEffect(() => {
    if (prevUser.current && !user) {
      disconnect();
    }
    prevUser.current = user;
  }, [user, disconnect]);

  const value = useMemo(
    () => ({
      client,
      connected,
      setConnected,
      deviceInfo,
      setDeviceInfo,
      serialLog,
      adoptClient,
      disconnect,
      suspend,
      resume,
      // Exposed so the page's first-connect watchdog can stand down while the
      // provider's armed watchdog owns post-loss reconnect — never two at once.
      autoReconnectEnabled,
    }),
    [client, connected, deviceInfo, serialLog, adoptClient, disconnect, suspend, resume, autoReconnectEnabled]
  );

  return (
    <SensorConnectionContext.Provider value={value}>
      {children}
    </SensorConnectionContext.Provider>
  );
}

export function useSensorConnection() {
  const ctx = useContext(SensorConnectionContext);
  if (ctx === null) {
    throw new Error('useSensorConnection must be used within a SensorConnectionProvider');
  }
  return ctx;
}
